import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

// Create the context
const TestContext = createContext(undefined);

// Custom hook to use the test context
export function useTest() {
    const context = useContext(TestContext);
    if (context === undefined) {
        throw new Error('useTest must be used within a TestProvider');
    }
    return context;
}

// Provider component
export function TestProvider({ children }) {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const refreshInProgress = useRef(false);
    const lastFetchTime = useRef(null);
    const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between fetches

    const fetchTests = useCallback(async (force = false) => {
        // Check if we're already fetching or if we've fetched recently
        if (refreshInProgress.current) {
            console.log('TestContext: Fetch already in progress, skipping');
            return null;
        }

        if (!force && lastFetchTime.current && Date.now() - lastFetchTime.current < FETCH_COOLDOWN) {
            console.log('TestContext: Fetch cooldown active, skipping');
            return null;
        }

        console.log('TestContext: fetchTests called');
        setLoading(true);
        setError(null);
        refreshInProgress.current = true;

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`, {
                params: { limit: 1000 },
            });

            console.log('TestContext: API Response:', response.data);

            if (response.data && Array.isArray(response.data.items)) {
                const newTests = response.data.items.map((test) => ({
                    ...test,
                    name: test.name || test.category,
                }));

                console.log('TestContext: Processed tests:', newTests);
                setTests(newTests);
                lastFetchTime.current = Date.now();
                return response.data;
            }
            throw new Error('Invalid response format');
        } catch (error) {
            console.error('TestContext: Error fetching tests:', error);
            setError(error.message || 'Error fetching tests. Please try again.');
            return null;
        } finally {
            setLoading(false);
            refreshInProgress.current = false;
        }
    }, []);

    const addTest = useCallback((newTest) => {
        console.log('TestContext: Adding new test:', newTest);
        if (!newTest?._id) {
            console.error('TestContext: Attempted to add test without _id');
            return;
        }

        setTests((prevTests) => {
            // First check if the test already exists
            const exists = prevTests.some((test) => test._id === newTest._id);
            if (exists) {
                // Update existing test
                return prevTests.map((test) => (test._id === newTest._id ? { ...newTest, name: newTest.name || newTest.category } : test));
            }
            // Add new test
            return [...prevTests, { ...newTest, name: newTest.name || newTest.category }];
        });
    }, []);

    const updateTest = useCallback((updatedTest) => {
        if (!updatedTest?._id) {
            console.error('TestContext: Attempted to update test without _id');
            return;
        }

        setTests((prevTests) =>
            prevTests.map((test) =>
                test._id === updatedTest._id ? { ...updatedTest, name: updatedTest.name || updatedTest.category } : test,
            ),
        );
    }, []);

    const deleteTest = useCallback((testId) => {
        if (!testId) {
            console.error('TestContext: Attempted to delete test without _id');
            return;
        }
        setTests((prev) => prev.filter((test) => test._id !== testId));
    }, []);

    const refreshTests = useCallback(
        async (force = false) => {
            console.log('TestContext: refreshTests called');
            return await fetchTests(force);
        },
        [fetchTests],
    );

    // Initial fetch only on mount
    useEffect(() => {
        console.log('TestContext: Component mounted, performing initial fetch');
        fetchTests(true); // Force initial fetch
    }, [fetchTests]);

    // Debug logging for tests state changes
    useEffect(() => {
        console.log('TestContext: Tests state updated:', tests);
    }, [tests]);

    const value = {
        tests,
        loading,
        error,
        fetchTests,
        addTest,
        updateTest,
        deleteTest,
        refreshTests,
    };

    return <TestContext.Provider value={value}>{children}</TestContext.Provider>;
}

// Export the context itself if needed elsewhere
export { TestContext };
